import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Palmtree,
  ExternalLink,
  LogIn,
  Home,
  Sparkles,
  Coins,
  Layers,
  Users,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import IsoIsland from "./oasis/IsoIsland";
import api from "@/lib/api";
import { t } from "@/i18n";

// ─────────────────────────────────────────────────────────────
// PAGE PUBLIQUE — consulter l'île d'un joueur via un lien partagé,
// sans compte requis.
//
// Montée sur /fr/oasis/s/:token, enveloppée dans MaybeShell (App.jsx) :
// connecté, tu vois ta vraie navbar d'app (Shell) ; pas connecté, la
// petite navbar publique ci-dessous prend le relais — jamais l'un des
// deux à la place de rien du tout.
//
// Aucune action n'y est possible : pas d'édition, pas d'achat, la prop
// `readOnly` d'IsoIsland le garantit côté rendu (candidates masquées)
// ET côté interaction (clic ignoré) — pas seulement parce que les
// callbacks passés sont des no-op.
// ─────────────────────────────────────────────────────────────

function PublicNav() {
  return (
    <header className="border-b bg-card">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
        <Link to="/fr" className="flex items-center gap-2 shrink-0">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center font-head font-bold text-primary arabic">
            ع
          </div>
          <span className="font-head font-extrabold text-lg hidden sm:block">
            {t("navbar.harf")}
          </span>
        </Link>
        <Link
          to="/fr"
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition ml-2"
        >
          <Home size={14} /> {t("coursequestions.accueil")}
        </Link>
        <div className="flex-1" />
        <Link
          to="/fr/auth"
          className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition"
        >
          {t("dictionary.commencer")}
        </Link>
      </div>
    </header>
  );
}

export default function PublicIsland() {
  const token = window.location.pathname.split("/fr").filter(Boolean).pop();
  const [state, setState] = useState({
    loading: true,
    error: null,
    ownerName: "",
    island: null,
  });
  const { user } = useAuth(); // null = chargement, false = non connecté, objet = connecté

  useEffect(() => {
    let alive = true;
    api
      .get(`/oasis/public/${token}`)
      .then((r) => {
        if (!alive) return;
        // Une réponse sans île exploitable (forme inattendue, champ
        // manquant) est traitée comme une erreur affichée — pas comme
        // une île vide silencieuse, qui plantait IsoIsland auparavant.
        if (!r.data?.island?.chunks)
          throw new Error("île absente de la réponse");
        setState({
          loading: false,
          error: null,
          ownerName: r.data.ownerName,
          island: r.data.island,
        });
      })
      .catch(() => {
        if (alive)
          setState({
            loading: false,
            error: "Ce lien ne mène à aucune île — il a peut-être expiré.",
            ownerName: "",
            island: null,
          });
      });
    return () => {
      alive = false;
    };
  }, [token]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex flex-col">
        {!user && <PublicNav />}
        <div className="flex-1 grid place-items-center text-muted-foreground animate-pulse">
          {t("publicisland.chargement_de_l_ile")}
        </div>
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen flex flex-col">
        {!user && <PublicNav />}
        <div className="flex-1 grid place-items-center p-6 text-center">
          <div>
            <p className="text-lg font-semibold">{state.error}</p>
            <Link
              to="/fr"
              className="inline-block mt-4 text-sm text-primary underline"
            >
              {t("publicisland.retour_a_l_accueil")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {!user && <PublicNav />}

      <div className="px-4 sm:px-6 py-4 flex items-center justify-between gap-3 border-b">
        <div className="flex items-center gap-2">
          <Palmtree size={20} className="text-primary" />
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">
              {t("publicisland.oasis_de")}
            </p>
            <p className="font-head font-bold leading-tight">
              {state.ownerName}
            </p>
          </div>
        </div>
        <span className="text-xs text-muted-foreground hidden sm:inline">
          {t("publicisland.consultation_seule_rien_n_est_modifiable")}
        </span>
      </div>

      <div className="flex-1 min-h-0">
        <IsoIsland
          island={state.island}
          editMode={null}
          pending={null}
          paintGround="dirt"
          balance={0}
          chunkCost={null}
          movingUid={null}
          onPlace={() => {}}
          onSelectPlaced={() => {}}
          onBuyChunk={() => {}}
          onPaint={() => {}}
          selectedPlaced={null}
          calibrate={false}
          adminSky={{ phase: null, rain: null }}
          onDropItem={() => {}}
          onCancelAction={() => {}}
          readOnly
        />
      </div>

      {/* ── Incitation à rejoindre Harf ────────────────────────── */}
      <div className="border-t bg-card">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 text-center">
          <Sparkles size={26} className="mx-auto text-primary mb-3" />
          <h2 className="font-head text-xl sm:text-2xl font-bold mb-2">
            {t("dictionaryword.harf_c_est_quoi")}
          </h2>
          <p className="text-muted-foreground max-w-lg mx-auto mb-6 leading-relaxed">
            {t("publicisland.une_application_pour_apprendre_l_arabe")}
          </p>

          <div className="grid sm:grid-cols-3 gap-3 max-w-xl mx-auto mb-7 text-left">
            <div className="rounded-2xl border bg-background p-4">
              <Coins size={18} className="text-primary mb-2" />
              <p className="text-sm font-semibold mb-1">{t("publicisland.gagne_des_dinars")}</p>
              <p className="text-xs text-muted-foreground">
                {t("publicisland.en_apprenant_chaque_jour")}
              </p>
            </div>
            <div className="rounded-2xl border bg-background p-4">
              <Layers size={18} className="text-primary mb-2" />
              <p className="text-sm font-semibold mb-1">{t("publicisland.construis_ton_ile")}</p>
              <p className="text-xs text-muted-foreground">
                {t("publicisland.batiments_decors_habitants")}
              </p>
            </div>
            <div className="rounded-2xl border bg-background p-4">
              <Users size={18} className="text-primary mb-2" />
              <p className="text-sm font-semibold mb-1">{t("publicisland.partage_la")}</p>
              <p className="text-xs text-muted-foreground">
                {t("publicisland.comme_celle_que_tu_regardes_la")}
              </p>
            </div>
          </div>

          {user ? (
            <a
              href="/recompenses"
              className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-semibold hover:brightness-110 transition"
            >
              {t("publicisland.voir_mon_ile")} <ExternalLink size={14} />
            </a>
          ) : (
            <>
              <a
                href="/fr/auth"
                className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 text-white px-6 py-3 text-sm font-bold hover:brightness-110 transition"
              >
                <LogIn size={14} /> {t("publicisland.creer_mon_ile_gratuitement")}
              </a>
              <p className="text-xs text-muted-foreground mt-3">
                {t("publicisland.7_jours_gratuits_sans_carte_bancaire")}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
